/**
 * Woody's Pets — Reviews System
 * File: assets/reviews.js
 *
 * Handles:
 *  - Firebase Firestore (review data storage)
 *  - Cloudinary (photo & video uploads — no Firebase Storage needed)
 *  - Real-time review display with filtering & sorting
 *  - Lightbox for photos/videos
 *
 * ─── SETUP ───────────────────────────────────────────────────
 * 1. Replace FIREBASE_CONFIG values with your project credentials
 *    Firebase Console → Project Settings → Your Apps → SDK setup
 *
 * 2. Replace CLOUDINARY_CLOUD_NAME with your cloud name
 *    Cloudinary Dashboard → top left (e.g. dsj1lstbc)
 *
 * 3. Replace CLOUDINARY_UPLOAD_PRESET with your preset name
 *    Cloudinary → Settings → Upload → Upload Presets (e.g. woodys-reviews)
 * ─────────────────────────────────────────────────────────────
 */

// ─────────────────────────────────────────────────────────────
// 1. FIREBASE CONFIG — replace all values here
// ─────────────────────────────────────────────────────────────
const FIREBASE_CONFIG = {
  apiKey: "AIzaSyAsgWqIk3PWEO-tVNyFSm6rH48AF_tl_TU",
  authDomain: "woodys-pets-reviews.firebaseapp.com",
  projectId: "woodys-pets-reviews",
  messagingSenderId: "437810251269",
  appId: "1:437810251269:web:07283b2bff98138cc5fe6c"
};

// ─────────────────────────────────────────────────────────────
// 2. CLOUDINARY CONFIG — replace both values here
// ─────────────────────────────────────────────────────────────
const CLOUDINARY_CLOUD_NAME    = "dsji1stbc";      // your cloud name
const CLOUDINARY_UPLOAD_PRESET = "woodysreviews"; // your unsigned preset name

// ─────────────────────────────────────────────────────────────
// 3. DISPLAY SETTINGS
// ─────────────────────────────────────────────────────────────
const REVIEWS_PER_PAGE = 9;    // cards shown before "Load more"
const REQUIRE_APPROVAL = true; // true = approve in Firestore before showing

// ─────────────────────────────────────────────────────────────
// 4. FIREBASE SDK (Firestore only — no Storage import needed)
// ─────────────────────────────────────────────────────────────
import { initializeApp }
  from "https://www.gstatic.com/firebasejs/10.12.0/firebase-app.js";
import { getFirestore, collection, addDoc, query, where,
         orderBy, onSnapshot, serverTimestamp }
  from "https://www.gstatic.com/firebasejs/10.12.0/firebase-firestore.js";

const app = initializeApp(FIREBASE_CONFIG);
const db  = getFirestore(app);

// ─────────────────────────────────────────────────────────────
// 5. ELEMENT REFERENCES
// ─────────────────────────────────────────────────────────────
const grid           = document.getElementById("wpReviewsGrid");
const loading        = document.getElementById("wpLoading");
const noResults      = document.getElementById("wpNoResults");
const loadMoreWrap   = document.getElementById("wpLoadMoreWrap");
const loadMoreBtn    = document.getElementById("wpLoadMore");
const avgRatingEl    = document.getElementById("wpAvgRating");
const totalEl        = document.getElementById("wpTotalReviews");
const sortSelect     = document.getElementById("wpSort");
const filterBtns     = document.querySelectorAll(".wp-pill[data-filter]");

const submitBtn      = document.getElementById("wpSubmitBtn");
const submitLabel    = document.getElementById("wpSubmitLabel");
const formError      = document.getElementById("wpFormError");
const successMsg     = document.getElementById("wpSuccess");
const reviewForm     = document.getElementById("wpReviewForm");
const starBtns       = document.querySelectorAll(".wp-star");
const mediaInput     = document.getElementById("wpMedia");
const uploadPH       = document.getElementById("wpUploadPlaceholder");
const uploadPreview  = document.getElementById("wpUploadPreview");
const previewImg     = document.getElementById("wpPreviewImg");
const previewVideo   = document.getElementById("wpPreviewVideo");
const removeMediaBtn = document.getElementById("wpRemoveMedia");
const progressWrap   = document.getElementById("wpUploadProgress");
const progressFill   = document.getElementById("wpProgressFill");
const progressText   = document.getElementById("wpProgressText");

const lightbox       = document.getElementById("wpLightbox");
const lightboxImg    = document.getElementById("wpLightboxImg");
const lightboxVideo  = document.getElementById("wpLightboxVideo");
const lightboxClose  = document.getElementById("wpLightboxClose");

// ─────────────────────────────────────────────────────────────
// 6. STATE
// ─────────────────────────────────────────────────────────────
let allReviews    = [];
let currentFilter = "all";
let currentSort   = "newest";
let displayCount  = REVIEWS_PER_PAGE;
let selectedRating = 5;
let selectedMedia  = null;

// ─────────────────────────────────────────────────────────────
// 7. STAR PICKER
// ─────────────────────────────────────────────────────────────
function setStars(n) {
  selectedRating = n;
  document.getElementById("wpRating").value = n;
  starBtns.forEach(s => s.classList.toggle("lit", parseInt(s.dataset.v) <= n));
}

starBtns.forEach(s => {
  s.addEventListener("click",      () => setStars(parseInt(s.dataset.v)));
  s.addEventListener("mouseover",  () => starBtns.forEach(b => b.classList.toggle("lit", parseInt(b.dataset.v) <= parseInt(s.dataset.v))));
  s.addEventListener("mouseleave", () => setStars(selectedRating));
});

// ─────────────────────────────────────────────────────────────
// 8. MEDIA PREVIEW
// ─────────────────────────────────────────────────────────────
mediaInput?.addEventListener("change", e => {
  const file = e.target.files[0];
  if (!file) return;

  if (file.size > 100 * 1024 * 1024) {
    showError("File must be under 100MB. Please choose a smaller file.");
    mediaInput.value = "";
    return;
  }

  selectedMedia = file;
  const url = URL.createObjectURL(file);
  uploadPH.style.display      = "none";
  uploadPreview.style.display = "block";

  if (file.type.startsWith("video")) {
    previewImg.style.display   = "none";
    previewVideo.style.display = "block";
    previewVideo.src = url;
  } else {
    previewVideo.style.display = "none";
    previewImg.style.display   = "block";
    previewImg.src = url;
  }
});

removeMediaBtn?.addEventListener("click", e => {
  e.stopPropagation();
  selectedMedia = null;
  mediaInput.value = "";
  uploadPreview.style.display = "none";
  uploadPH.style.display      = "block";
  previewImg.src   = "";
  previewVideo.src = "";
});

// ─────────────────────────────────────────────────────────────
// 9. CLOUDINARY UPLOAD
//    Uses the unsigned upload API — no secret key exposed
// ─────────────────────────────────────────────────────────────
async function uploadToCloudinary(file) {
  const resourceType = file.type.startsWith("video") ? "video" : "image";
  const url = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/${resourceType}/upload`;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
  formData.append("folder", "reviews");

  progressWrap.style.display = "block";
  progressFill.style.width   = "60%";
  progressText.textContent   = "Uploading…";

  const response = await fetch(url, {
    method: "POST",
    body: formData
  });

  progressWrap.style.display = "none";

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || `Upload failed: ${response.status}`);
  }

  const data = await response.json();
  return {
    url:  data.secure_url,
    type: resourceType
  };
}

// ─────────────────────────────────────────────────────────────
// 10. FORM SUBMISSION
// ─────────────────────────────────────────────────────────────
submitBtn?.addEventListener("click", handleSubmit);

async function handleSubmit() {
  const name   = document.getElementById("wpName")?.value.trim();
  const email  = document.getElementById("wpEmail")?.value.trim();
  const body   = document.getElementById("wpReview")?.value.trim();
  const rating = selectedRating;

  clearError();

  if (!name)                          return showError("Please enter your name.");
  if (!email || !email.includes("@")) return showError("Please enter a valid email address.");
  if (!body)                          return showError("Please write your review.");
  if (rating < 1)                     return showError("Please select a star rating.");

  setSubmitting(true);

  try {
    let mediaUrl  = null;
    let mediaType = null;

    if (selectedMedia) {
      const result = await uploadToCloudinary(selectedMedia);
      mediaUrl  = result.url;
      mediaType = result.type;
    }

    await addDoc(collection(db, "reviews"), {
      name,
      email,
      body,
      rating,
      mediaUrl,
      mediaType,
      approved:  !REQUIRE_APPROVAL,
      featured:  false,
      createdAt: serverTimestamp()
    });

    reviewForm.style.display = "none";
    successMsg.style.display = "block";

    // Analytics: announce a successful submission (star rating only, no personal data)
    try {
      window.Shopify?.analytics?.publish?.("review_submitted", {
        rating: rating,
        has_media: !!mediaUrl
      });
    } catch (e) {
      console.warn("Analytics event failed:", e);
    }

  } catch (err) {
    console.error("Review submission error:", err);
    showError("Something went wrong — please try again or email us directly.");
    setSubmitting(false);
    return;
  }

  // Send email notification
  // Outside main try/catch so review submission always succeeds regardless
  try {
  const emailRes = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      service_id:  "service_k8p5c95",
      template_id: "template_in2wdic",
      user_id:     "BPKvTwX3AhNJ6d-qI",
      template_params: {
        reviewer_name: name,
        body:          body,
        rating:        rating,
        name:          name,
        email:         email
      }
    })
  });
  const emailText = await emailRes.text();
  console.log("EmailJS response:", emailRes.status, emailText);
} catch(e) {
  console.warn("Email notification failed:", e);
}
}

function setSubmitting(on) {
  submitBtn.disabled      = on;
  submitLabel.textContent = on ? "Submitting…" : "Submit my review";
}

function showError(msg) {
  formError.textContent   = msg;
  formError.style.display = "block";
  formError.scrollIntoView({ behavior: "smooth", block: "nearest" });
}

function clearError() {
  formError.textContent   = "";
  formError.style.display = "none";
}

// ─────────────────────────────────────────────────────────────
// 11. REAL-TIME REVIEWS LISTENER
// ─────────────────────────────────────────────────────────────
function startReviewListener() {
  if (!grid) return;

  const q = query(
    collection(db, "reviews"),
    where("approved", "==", true),
    orderBy("createdAt", "desc")
  );

  onSnapshot(q, snapshot => {
    allReviews = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    updateSummaryStats();
    renderReviews();
    if (loading) loading.style.display = "none";
  }, err => {
    console.error("Firestore listener error:", err);
    if (loading) loading.innerHTML = "<p>Could not load reviews. Please refresh the page.</p>";
  });
}

// ─────────────────────────────────────────────────────────────
// 12. FILTER & SORT
// ─────────────────────────────────────────────────────────────
filterBtns.forEach(btn => {
  btn.addEventListener("click", () => {
    filterBtns.forEach(b => b.classList.remove("active"));
    btn.classList.add("active");
    currentFilter = btn.dataset.filter;
    displayCount  = REVIEWS_PER_PAGE;
    renderReviews();
  });
});

sortSelect?.addEventListener("change", () => {
  currentSort  = sortSelect.value;
  displayCount = REVIEWS_PER_PAGE;
  renderReviews();
});

function getFiltered() {
  let reviews = [...allReviews];

  if (currentFilter === "photo")             reviews = reviews.filter(r => r.mediaType === "image");
  else if (currentFilter === "video")        reviews = reviews.filter(r => r.mediaType === "video");
  else if (!isNaN(parseInt(currentFilter))) reviews = reviews.filter(r => r.rating === parseInt(currentFilter));

  if (currentSort === "oldest")        reviews.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
  else if (currentSort === "highest")  reviews.sort((a, b) => b.rating - a.rating);
  else                                 reviews.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

  reviews.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  return reviews;
}

// ─────────────────────────────────────────────────────────────
// 13. RENDER
// ─────────────────────────────────────────────────────────────
function renderReviews() {
  if (!grid) return;

  const filtered = getFiltered();
  const visible  = filtered.slice(0, displayCount);

  if (filtered.length === 0) {
    grid.innerHTML             = "";
    noResults.style.display    = "block";
    loadMoreWrap.style.display = "none";
    return;
  }

  noResults.style.display    = "none";
  grid.innerHTML             = visible.map(buildCard).join("");
  loadMoreWrap.style.display = filtered.length > displayCount ? "block" : "none";

  grid.querySelectorAll("[data-lightbox]").forEach(el => {
    el.addEventListener("click", () => openLightbox(el.dataset.lightbox, el.dataset.lbtype));
  });
}

function buildCard(r) {
  const stars    = "★".repeat(r.rating) + "☆".repeat(5 - r.rating);
  const date     = r.createdAt ? formatDate(r.createdAt.seconds * 1000) : "";
  const media    = r.mediaUrl  ? buildMedia(r) : "";
  const featured = r.featured  ? `<div class="wp-featured-badge">Featured review</div>` : "";

  return `
    <div class="wp-review-card${r.featured ? " featured" : ""}">
      ${featured}
      ${media}
      <div class="wp-card-body">
        <div class="wp-card-header">
          <div class="wp-card-name">${escHtml(r.name)}</div>
          <div class="wp-card-date">${date}</div>
        </div>
        <div class="wp-card-stars" aria-label="${r.rating} stars">${stars}</div>
        <div class="wp-card-text">${escHtml(r.body)}</div>
        <span class="wp-verified-badge">Verified purchase</span>
      </div>
    </div>`;
}

function buildMedia(r) {
  if (r.mediaType === "video") {
    return `<video class="wp-card-media-video" src="${r.mediaUrl}"
              data-lightbox="${r.mediaUrl}" data-lbtype="video"
              muted playsinline preload="metadata"
              style="cursor:pointer;"
              onclick="this.paused ? this.play() : this.pause()"></video>`;
  }
  return `<img class="wp-card-media" src="${r.mediaUrl}" alt="Customer photo"
            loading="lazy" data-lightbox="${r.mediaUrl}" data-lbtype="image">`;
}

// ─────────────────────────────────────────────────────────────
// 14. LOAD MORE
// ─────────────────────────────────────────────────────────────
loadMoreBtn?.addEventListener("click", () => {
  displayCount += REVIEWS_PER_PAGE;
  renderReviews();
});

// ─────────────────────────────────────────────────────────────
// 15. SUMMARY STATS
// ─────────────────────────────────────────────────────────────
function updateSummaryStats() {
  if (!allReviews.length) return;
  const avg = allReviews.reduce((s, r) => s + r.rating, 0) / allReviews.length;
  if (avgRatingEl) avgRatingEl.textContent = avg.toFixed(1);
  if (totalEl)     totalEl.textContent     = allReviews.length + "+";
}

// ─────────────────────────────────────────────────────────────
// 16. LIGHTBOX
// ─────────────────────────────────────────────────────────────
function openLightbox(url, type) {
  if (type === "video") {
    lightboxImg.style.display   = "none";
    lightboxVideo.style.display = "block";
    lightboxVideo.src = url;
  } else {
    lightboxVideo.style.display = "none";
    lightboxImg.style.display   = "block";
    lightboxImg.src = url;
  }
  lightbox.classList.add("open");
  document.body.style.overflow = "hidden";
}

function closeLightbox() {
  lightbox.classList.remove("open");
  lightboxVideo.pause?.();
  lightboxVideo.src = "";
  lightboxImg.src   = "";
  document.body.style.overflow = "";
}

lightboxClose?.addEventListener("click", closeLightbox);
lightbox?.addEventListener("click", e => { if (e.target === lightbox) closeLightbox(); });
document.addEventListener("keydown", e => { if (e.key === "Escape") closeLightbox(); });

// ─────────────────────────────────────────────────────────────
// 17. HELPERS
// ─────────────────────────────────────────────────────────────
function formatDate(ms) {
  return new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function escHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ─────────────────────────────────────────────────────────────
// 18. BOOT
// ─────────────────────────────────────────────────────────────
startReviewListener();